/*
 * Elexa offers (group A2). Loaded deferred by every A2 product block (after elexa-core.js).
 *
 * Elexa.offers: the page-level offer engine, one state per product section.
 *   Elexa.offers.get(sectionId)      last `elexa:offer-change` detail of that section (or null)
 *   Elexa.offers.section(sectionId)  the section state (used by the blocks below)
 *
 * Events (CustomEvent on document):
 *   elexa:offer-change         { sectionId, items, label, totalCents, compareCents, quantity,
 *                                subscription, valid, error, source }
 *     items: the full list the main Add to cart must post, in order: gift lines, main lines
 *            (each with selling_plan when subscribed, `_elexa_offer` when tagged), add-on lines.
 *            null = no offer is active: the product form posts its own fields (Dawn default).
 *     totalCents / compareCents: tier totals for the price block and the "price in button" label,
 *            null when no quantity offer is active (show the normal price).
 *     valid false: a per-unit row is unavailable or the tier breaks the quantity rules; don't add.
 *   elexa:subscription-change  { sectionId, sellingPlanId, type, value, unitCents, compareUnitCents, variantId }
 *
 * Fallbacks while the product form is not offer-aware (Elexa.offerAware !== true, e.g. plain Dawn):
 * the engine posts `items` itself through Elexa.cart.add, writes tier totals into Dawn's price block
 * (#price-<section id>) and hides Dawn's quantity selector while a quantity offer drives the form.
 * It always keeps the form's `selling_plan` field in sync and blocks the submit while invalid.
 *
 * Custom elements: <elexa-qty-breaks>, <elexa-subscribe-save>, <elexa-gift-kit>, <elexa-upsell>,
 * <elexa-bundle-save>.
 */
(() => {
  const Elexa = (window.Elexa = window.Elexa || {});
  if (Elexa.offers && Elexa.offers.loaded) return;

  const EVENTS = Object.assign(
    { variantChange: 'elexa:variant-change', offerChange: 'elexa:offer-change', cartUpdated: 'elexa:cart-updated' },
    Elexa.EVENTS || {}
  );
  const SUBSCRIPTION_EVENT = 'elexa:subscription-change';

  /* ------------------------------------------------------------------ *
   * Utilities
   * ------------------------------------------------------------------ */
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const money = (cents) =>
    typeof Elexa.money === 'function' ? Elexa.money(cents) : (Math.round(cents) / 100).toFixed(2);
  const priceMoney = (cents) =>
    Elexa.config && Elexa.config.currencyCodeEnabled && typeof Elexa.moneyWithCurrency === 'function'
      ? Elexa.moneyWithCurrency(cents)
      : money(cents);
  const sum = (list) => list.reduce((a, b) => a + b, 0);
  const savePct = (compare, total) => (compare > 0 && compare > total ? Math.round(((compare - total) * 100) / compare) : 0);
  const fill = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const escapeHTML = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const textOf = (html) => new DOMParser().parseFromString(String(html || ''), 'text/html').body.textContent.trim();

  function readJSON(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[Elexa offers] bad JSON in', selector, e);
      return null;
    }
  }

  /** Storefront currency rate (shop currency → shopper currency). */
  function rate() {
    const r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return r > 0 ? r : 1;
  }

  /** Merchant-typed amount in the shop currency ("24.99", "24,99", "1,234.50") → shopper cents, or null. */
  function typedCents(value) {
    let s = String(value == null ? '' : value).trim().replace(/\s/g, '');
    if (!s) return null;
    if (s.includes(',') && s.includes('.')) {
      s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
    } else {
      s = s.replace(',', '.');
    }
    if (!/^\d*\.?\d+$/.test(s)) return null;
    return Math.round(parseFloat(s) * 100 * rate());
  }

  /** Fill [data-ex-typed] spans (typed amounts) with the converted, formatted amount. */
  function hydrateTyped(root) {
    root.querySelectorAll('[data-ex-typed]').forEach((el) => {
      const cents = typedCents(el.dataset.exTyped);
      if (cents !== null) el.innerHTML = money(cents);
    });
  }

  const SIZE_ABBR = {
    'extra small': 'XS', 'x-small': 'XS', xsmall: 'XS', small: 'S', medium: 'M', large: 'L',
    'extra large': 'XL', 'x-large': 'XL', xlarge: 'XL', 'xx-large': '2XL', xxlarge: '2XL', xxl: '2XL', '2xl': '2XL',
    'xxx-large': '3XL', xxxl: '3XL', '3xl': '3XL', 'one size': 'OS', onesize: 'OS', petite: 'P',
  };
  const abbreviateSize = (name) => SIZE_ABBR[String(name).trim().toLowerCase()] || name;

  const BASIC_COLOURS = {
    red: '#E53935', blue: '#1E63D6', green: '#2E9E4F', yellow: '#F5C518', orange: '#F57C00', purple: '#7B3FB6',
    pink: '#F48FB1', brown: '#795548', black: '#111111', white: '#FFFFFF', grey: '#9E9E9E', gray: '#9E9E9E',
    navy: '#1A2A57', beige: '#E8DCC4',
  };
  function swatchColour(value) {
    if (value.swatch) return value.swatch;
    const key = String(value.name).trim().toLowerCase();
    const map = (Elexa.config && Elexa.config.settings && Elexa.config.settings.swatches) || {};
    if (map[key]) return map[key];
    return BASIC_COLOURS[key] || '#CCCCCC';
  }

  /** Normalise a variant object (ours or Shopify's) to { id, price, compare, available, options, ... }. */
  function normaliseVariant(v) {
    if (!v) return null;
    return {
      id: Number(v.id),
      title: v.title || '',
      price: Number(v.price) || 0,
      compare: Number(v.compare != null ? v.compare : v.compare_at_price) || 0,
      available: v.available !== false,
      options: v.options || [v.option1, v.option2, v.option3].filter((o) => o != null),
      image: v.image || null,
      mediaId: v.mediaId || (v.featured_media && v.featured_media.id) || null,
      qmin: v.qmin || (v.quantity_rule && v.quantity_rule.min) || 1,
      qmax: v.qmax || (v.quantity_rule && v.quantity_rule.max) || null,
      qinc: v.qinc || (v.quantity_rule && v.quantity_rule.increment) || 1,
      plans: v.plans || null,
    };
  }

  const ruleAllows = (variant, q) =>
    !variant || (q >= (variant.qmin || 1) && (!variant.qmax || q <= variant.qmax) && q % (variant.qinc || 1) === 0);

  /* ------------------------------------------------------------------ *
   * Offer engine
   * ------------------------------------------------------------------ */
  const sections = new Map();
  const formIndex = new Map();

  class OfferSection {
    constructor(id) {
      this.id = String(id);
      this.formId = `product-form-${this.id}`;
      this.blocks = new Set();
      this.variants = new Map();
      this.product = null;
      this.variantId = null;
      this.lastVariant = null;
      this.subscription = null;
      this.subscriptionOwner = null;
      this.gifts = new Map();
      this.addons = new Map();
      this.baseQty = null;
      this.baseQtyOwner = null;
      this.detail = null;
      this.queued = false;
      this.busy = false;
      this.disabledByOffer = false;
      this.lockedQuantity = [];
    }

    /* Registration ----------------------------------------------------- */
    register(block) {
      this.blocks.add(block);
      if (block.formId) {
        this.formId = block.formId;
        formIndex.set(block.formId, this.id);
      }
      if (block.mainProductData) this.addProduct(block.mainProductData);
      if (!this.variants.size) this.seedFromProduct();
      const id = this.readVariantId();
      if (id && this.variants.has(id)) this.variantId = id;
      if (this.variant) this.lastVariant = this.variant;
      this.schedule();
    }

    unregister(block) {
      this.blocks.delete(block);
      this.gifts.delete(block);
      this.addons.delete(block);
      if (this.subscriptionOwner === block) {
        this.subscription = null;
        this.subscriptionOwner = null;
      }
      if (this.baseQtyOwner === block) {
        this.baseQty = null;
        this.baseQtyOwner = null;
      }
      this.schedule();
    }

    /** Blocks without product data (gift kit, upsell): take the variants from the section's <elexa-product>. */
    seedFromProduct() {
      const ctrl = Elexa.products && Elexa.products[this.id];
      if (ctrl && Array.isArray(ctrl.variants) && ctrl.variants.length) this.addProduct({ variants: ctrl.variants });
    }

    addProduct(data) {
      if (!data || !Array.isArray(data.variants)) return;
      if (!this.product) this.product = data;
      data.variants.forEach((v) => {
        const id = String(v.id);
        const prev = this.variants.get(id);
        const next = normaliseVariant(v);
        if (prev && prev.plans && !next.plans) next.plans = prev.plans;
        this.variants.set(id, next);
      });
      if (!this.variantId && data.selected) this.variantId = String(data.selected);
    }

    /* Form and variant -------------------------------------------------- */
    get form() {
      return document.getElementById(this.formId);
    }

    readVariantId() {
      const input = this.form && this.form.querySelector('[name="id"]');
      if (input) return input.value ? String(input.value) : null;
      return this.variantId;
    }

    get variant() {
      return (this.variantId && this.variants.get(String(this.variantId))) || null;
    }

    /** The variant offers price against: the selected one, or the last valid one while unavailable. */
    get displayVariant() {
      return this.variant || this.lastVariant;
    }

    setVariantId(id, external) {
      const next = id ? String(id) : null;
      if (next && external && !this.variants.has(next)) this.variants.set(next, normaliseVariant(external));
      if (next === this.variantId) {
        this.schedule();
        return;
      }
      const prev = this.displayVariant;
      this.variantId = next;
      const current = this.variant;
      if (current) this.lastVariant = current;
      this.blocks.forEach((b) => b.onVariantChange && b.onVariantChange(current || prev, prev));
      if (this.subscription) this.publishSubscription();
      this.schedule();
    }

    qtyInput() {
      return (
        document.querySelector(`input[name="quantity"][form="${this.formId}"]`) ||
        (this.form && this.form.querySelector('input[name="quantity"]'))
      );
    }

    baseQuantity() {
      const input = this.qtyInput();
      if (input && !input.closest('.ex-offer-locked')) {
        const n = parseInt(input.value, 10);
        if (n > 0) return n;
      }
      return this.baseQty || 1;
    }

    setBaseQuantity(n, owner) {
      this.baseQty = n;
      this.baseQtyOwner = owner;
      const input = this.qtyInput();
      if (input) {
        input.value = n;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.schedule();
    }

    clearBaseQuantity(owner) {
      if (this.baseQtyOwner !== owner) return;
      this.baseQty = null;
      this.baseQtyOwner = null;
      const input = this.qtyInput();
      if (input) {
        input.value = Math.max(1, parseInt(input.min, 10) || 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.schedule();
    }

    /* Contributors ------------------------------------------------------ */
    activeQuantityOffer() {
      const list = [...this.blocks].filter((b) => b.isQuantityOffer && b.isConnected && b.isActive());
      list.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      return list[0] || null;
    }

    setSubscription(owner, sub) {
      this.subscription = sub;
      this.subscriptionOwner = sub ? owner : null;
      this.blocks.forEach((b) => b !== owner && b.onSubscriptionChange && b.onSubscriptionChange(sub));
      this.publishSubscription();
      this.schedule();
    }

    publishSubscription() {
      const sub = this.subscription;
      const v = this.displayVariant;
      emit(SUBSCRIPTION_EVENT, {
        sectionId: this.id,
        sellingPlanId: sub ? Number(sub.planId) : null,
        type: sub ? sub.type : null,
        value: sub ? sub.value : null,
        unitCents: sub && v ? this.subscriptionUnit(v) : null,
        compareUnitCents: v ? Math.max(v.compare || 0, v.price) : null,
        variantId: v ? v.id : null,
      });
    }

    /** Per-unit subscription price of a variant under the active plan (Shopify's allocation price). */
    subscriptionUnit(variant, sub = this.subscription) {
      if (!sub || !variant) return variant ? variant.price : 0;
      const known = this.variants.get(String(variant.id));
      const plans = (known && known.plans) || variant.plans;
      if (plans && plans[sub.planId] != null) return Number(plans[sub.planId]);
      const p = variant.price;
      if (sub.type === 'percentage') return Math.max(0, Math.round(p * (1 - sub.value / 100)));
      if (sub.type === 'fixed_amount') return Math.max(0, p - Math.round(sub.value));
      if (sub.type === 'price') return Math.max(0, Math.round(sub.value));
      return p;
    }

    /** Subscription total for standard-priced units (offer-cart-contract §3.4). */
    subscriptionTotal(units, standardTotal) {
      const sub = this.subscription;
      if (!sub) return standardTotal;
      if (sub.type === 'percentage') return Math.max(0, Math.round(standardTotal * (1 - sub.value / 100)));
      return Math.max(0, sum(units.map((u) => this.subscriptionUnit(u, sub))));
    }

    setGifts(owner, list) {
      this.gifts.set(owner, list || []);
      this.schedule();
    }

    setAddons(owner, list) {
      this.addons.set(owner, list || []);
      this.schedule();
    }

    /* Broadcast --------------------------------------------------------- */
    schedule() {
      if (this.queued) return;
      this.queued = true;
      queueMicrotask(() => {
        this.queued = false;
        this.emit();
      });
    }

    build() {
      const qo = this.activeQuantityOffer();
      const state = qo ? qo.offerState() : null;
      const sub = this.subscription;
      const variant = this.variant;
      let main = [];
      let valid = true;
      let error = '';
      if (state) {
        main = state.lines;
        valid = state.valid !== false;
        error = state.error || '';
      } else if (variant) {
        main = [{ id: variant.id, quantity: this.baseQuantity() }];
      }
      const label = state ? state.label || '' : '';
      const mainQty = sum(main.map((l) => l.quantity));

      const mainLines = main.map((l) => {
        const item = { id: Number(l.id), quantity: l.quantity };
        if (state && state.tag && label) item.properties = { _elexa_offer: label };
        if (sub) item.selling_plan = Number(sub.planId);
        return item;
      });

      const seen = new Set();
      const giftLines = [];
      this.gifts.forEach((list) =>
        list.forEach((g) => {
          if (!g || !g.id || seen.has(String(g.id))) return;
          seen.add(String(g.id));
          giftLines.push({
            id: Number(g.id),
            quantity: Math.max(1, mainQty),
            properties: { _elexa_gift: 'linked', _elexa_gift_parent: String(g.parent) },
          });
        })
      );

      const addonLines = [];
      this.addons.forEach((list) =>
        list.forEach((a) => a && a.id && addonLines.push({ id: Number(a.id), quantity: a.quantity || 1 }))
      );

      const takeover = Boolean(state || sub || giftLines.length || addonLines.length);
      const items = takeover && mainLines.length ? [...giftLines, ...mainLines, ...addonLines] : null;

      return {
        sectionId: this.id,
        items,
        label,
        totalCents: state && state.totalCents != null ? state.totalCents : null,
        compareCents: state && state.compareCents != null ? state.compareCents : null,
        quantity: mainQty,
        subscription: sub ? { sellingPlanId: Number(sub.planId), type: sub.type, value: sub.value, name: sub.name || '' } : null,
        valid,
        error,
        source: 'elexa-offers',
      };
    }

    emit() {
      const detail = this.build();
      this.detail = detail;
      emit(EVENTS.offerChange, detail);
      this.applyFallbacks(detail);
      this.blocks.forEach((b) => b.onOfferChange && b.onOfferChange(detail));
    }

    /* Fallbacks for a plain (not offer-aware) product form -------------- */
    applyFallbacks(detail) {
      const aware = Elexa.offerAware === true;
      const quantityOffer = detail.totalCents != null;
      this.lockQuantitySelector(quantityOffer);
      this.syncSellingPlanInput();
      if (!aware && quantityOffer) this.writeDawnPrice(detail.totalCents, detail.compareCents);
      this.markSubmit(detail.valid);
    }

    lockQuantitySelector(lock) {
      const nodes = [
        document.getElementById(`Quantity-Form-${this.id}`),
        ...document.querySelectorAll(`[data-ex-quantity-selector="${this.id}"]`),
      ].filter(Boolean);
      nodes.forEach((el) => el.classList.toggle('ex-offer-locked', lock));
    }

    syncSellingPlanInput() {
      const form = this.form;
      if (!form) return;
      let input =
        form.querySelector('input[name="selling_plan"]') ||
        document.querySelector(`input[name="selling_plan"][form="${this.formId}"]`);
      const plan = this.subscription ? String(this.subscription.planId) : '';
      if (!input) {
        if (!plan) return;
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.setAttribute('data-ex-offers', '');
        form.appendChild(input);
      }
      input.value = plan;
      if (input.hasAttribute('data-ex-offers')) input.disabled = !plan;
    }

    writeDawnPrice(total, compare) {
      const box = document.getElementById(`price-${this.id}`);
      const price = box && box.querySelector('.price');
      if (!price) return;
      const onSale = compare != null && compare > total;
      price
        .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
        .forEach((el) => (el.innerHTML = priceMoney(total)));
      const struck = price.querySelector('.price__sale s.price-item--regular');
      if (struck) struck.innerHTML = onSale ? priceMoney(compare) : '';
      price.classList.toggle('price--on-sale', onSale);
    }

    submitButton() {
      const form = this.form;
      return form ? form.querySelector('[type="submit"]') : null;
    }

    markSubmit(valid) {
      const btn = this.submitButton();
      if (!btn) return;
      btn.classList.toggle('ex-offer-invalid', !valid);
      if (!valid) {
        if (btn.getAttribute('aria-disabled') !== 'true') {
          btn.setAttribute('aria-disabled', 'true');
          this.disabledByOffer = true;
        }
      } else if (this.disabledByOffer) {
        btn.removeAttribute('aria-disabled');
        this.disabledByOffer = false;
      }
    }

    errorBox() {
      const form = this.form;
      const pf = form && form.closest('product-form');
      return pf ? pf.querySelector('.product-form__error-message-wrapper') : null;
    }

    showError(message) {
      const wrapper = this.errorBox();
      if (!wrapper) return;
      const text = wrapper.querySelector('.product-form__error-message');
      wrapper.toggleAttribute('hidden', !message);
      if (text && message) text.textContent = message;
    }

    /** Fallback add for a plain Dawn product form. */
    async submit() {
      const detail = this.build();
      if (!detail.items || this.busy) return;
      const btn = this.submitButton();
      if (btn && btn.getAttribute('aria-disabled') === 'true' && !this.disabledByOffer) return;
      this.busy = true;
      this.showError(false);
      const spinner = btn && btn.querySelector('.loading__spinner');
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');
      const hasGifts = detail.items.some((i) => i.properties && i.properties._elexa_gift);
      try {
        await Elexa.cart.add(detail.items, { open: true, source: 'product-form' });
      } catch (err) {
        let message = err && err.message;
        if (hasGifts) {
          // A gift that sold out since page load must never block the main purchase.
          try {
            await Elexa.cart.add(detail.items.filter((i) => !(i.properties && i.properties._elexa_gift)), {
              open: true,
              source: 'product-form',
            });
            message = null;
          } catch (err2) {
            message = err2 && err2.message;
          }
        }
        if (message) this.showError(message);
      } finally {
        this.busy = false;
        if (btn) {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');
        this.markSubmit(detail.valid);
      }
    }
  }

  const getSection = (id) => {
    const key = String(id);
    if (!sections.has(key)) sections.set(key, new OfferSection(key));
    return sections.get(key);
  };

  Elexa.offers = {
    loaded: true,
    section: getSection,
    get: (id) => (sections.has(String(id)) ? sections.get(String(id)).detail : null),
    items: (id) => (sections.has(String(id)) ? (sections.get(String(id)).detail || {}).items || null : null),
    typedCents,
    money,
  };

  /* Global listeners ----------------------------------------------------- */
  const sectionForForm = (formId) => (formIndex.has(formId) ? sections.get(formIndex.get(formId)) : null);
  // Read the attribute: a form's `id` property is shadowed by its <input name="id">.
  const formIdOf = (form) => (form ? form.getAttribute('id') : null);

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.name === 'id' && t.form) {
      const s = sectionForForm(formIdOf(t.form));
      if (s) s.setVariantId(t.value);
    } else if (t.name === 'quantity') {
      const s = sectionForForm(t.getAttribute('form') || formIdOf(t.form));
      if (s) s.schedule();
    }
  });
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.name !== 'quantity') return;
    const s = sectionForForm(t.getAttribute('form') || formIdOf(t.form));
    if (s) s.schedule();
  });
  // The product controller can upgrade after the offer blocks: pick up its variants and selection then.
  document.addEventListener('elexa:product-ready', (e) => {
    const d = e.detail || {};
    const s = d.sectionId && sections.get(String(d.sectionId));
    if (!s) return;
    if (d.product && Array.isArray(d.product.variants)) s.addProduct({ variants: d.product.variants });
    const id = s.readVariantId() || (d.variant ? String(d.variant.id) : null);
    if (id && s.variants.has(id)) s.setVariantId(id);
  });
  document.addEventListener(EVENTS.variantChange, (e) => {
    const d = e.detail || {};
    if (!d.sectionId || !sections.has(String(d.sectionId))) return;
    sections.get(String(d.sectionId)).setVariantId(d.variant ? d.variant.id : null, d.variant);
  });

  // Submit: block invalid offers always; post the items ourselves only for a plain Dawn form.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const s = sectionForForm(formIdOf(form));
      if (!s) return;
      const detail = s.build();
      if (detail.valid === false) {
        e.preventDefault();
        e.stopImmediatePropagation();
        s.blocks.forEach((b) => b.revealError && b.revealError());
        return;
      }
      if (Elexa.offerAware === true || !detail.items) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      s.submit();
    },
    true
  );

  function subscribeDawn() {
    if (typeof window.subscribe !== 'function' && typeof subscribe !== 'function') return;
    const sub = typeof window.subscribe === 'function' ? window.subscribe : subscribe; // eslint-disable-line no-undef
    const ev = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null); // eslint-disable-line no-undef
    if (!ev) return;
    sub(ev.variantChange, (event) => {
      const data = (event && event.data) || {};
      const s = sections.get(String(data.sectionId));
      if (!s) return;
      // Dawn has just re-rendered the price HTML: re-apply after its update finishes.
      s.setVariantId(data.variant ? data.variant.id : null, data.variant);
      s.schedule();
    });
    sub(ev.quantityUpdate, () => sections.forEach((s) => s.schedule()));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeDawn);
  else subscribeDawn();

  /* ------------------------------------------------------------------ *
   * Base class for blocks that join a section's offer state
   * ------------------------------------------------------------------ */
  class OfferBlock extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.sectionId = this.dataset.sectionId;
        this.blockId = this.dataset.blockId;
        this.formId = this.dataset.formId;
        this.config = readJSON(this, ':scope > script[data-ex-config]') || {};
        hydrateTyped(this);
        this.init();
      }
      this.engine = getSection(this.sectionId);
      this.cleanup = [];
      this.connect();
      this.engine.register(this);
      this.afterRegister();
    }

    disconnectedCallback() {
      if (this.engine) this.engine.unregister(this);
      (this.cleanup || []).forEach((fn) => fn());
      this.cleanup = [];
    }

    /** Document-level listeners; removed again on disconnect. */
    listen(target, name, handler) {
      target.addEventListener(name, handler);
      this.cleanup.push(() => target.removeEventListener(name, handler));
    }

    init() {}
    connect() {}
    afterRegister() {}
  }

  /* ------------------------------------------------------------------ *
   * <elexa-qty-breaks>: Quantity breaks (list / cards / grid)
   * ------------------------------------------------------------------ */
  class QtyBreaks extends OfferBlock {
    init() {
      this.isQuantityOffer = true;
      this.mainProductData = readJSON(this, ':scope > script[data-ex-product]');
      this.product = this.mainProductData || { options: [], variants: [] };
      this.allVariants = (this.product.variants || []).map(normaliseVariant);
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      this.selected = this.tiers.find((t) => t.querySelector('[data-ex-tier-input]').checked) || this.tiers[0];
      this.rows = [];
      this.strings = this.config.strings || {};

      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.matches('[data-ex-tier-input]')) {
          this.select(t.closest('[data-tier]'));
        } else if (t.matches('select[data-row]')) {
          this.setRowValue(Number(t.dataset.row), Number(t.dataset.pos), t.value);
        }
      });
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-row]');
        if (btn) {
          e.preventDefault();
          if (!btn.disabled) this.setRowValue(Number(btn.dataset.row), Number(btn.dataset.pos), btn.dataset.value);
          return;
        }
        const tier = e.target.closest('[data-tier]');
        if (!tier || e.target.closest('[data-rows], label, input, select, a, button')) return;
        this.select(tier);
      });
    }

    afterRegister() {
      this.renderRows();
      this.update();
    }

    isActive() {
      return this.tiers.length > 0;
    }

    mainVariant() {
      return this.engine.displayVariant || this.allVariants[0] || null;
    }

    tierConfig(tier) {
      return (this.config.tiers || {})[tier.dataset.tier] || {};
    }

    rowsActive(tier) {
      const mode = this.config.perUnit;
      if (!mode || mode === 'off' || this.allVariants.length < 2) return false;
      if (!this.shownPositions().length) return false;
      return mode === 'all_tiers' || Number(tier.dataset.qty) > 1;
    }

    shownPositions() {
      const display = this.config.rowDisplay || [];
      return (this.product.options || [])
        .filter((o) => (o.values || []).length > 1 && display[o.position - 1] && display[o.position - 1] !== 'hidden')
        .map((o) => o.position);
    }

    select(tier) {
      if (!tier || tier.classList.contains('is-disabled')) return;
      this.selected = tier;
      const input = tier.querySelector('[data-ex-tier-input]');
      if (input && !input.checked) input.checked = true;
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
      this.renderRows();
      this.update();
    }

    /* Per-unit rows ----------------------------------------------------- */
    ensureRows(q) {
      const main = this.mainVariant();
      while (this.rows.length < q) this.rows.push(main ? main.options.slice() : []);
    }

    rowValues(i) {
      const main = this.mainVariant();
      const shown = this.shownPositions();
      const values = (this.rows[i] || []).slice();
      (this.product.options || []).forEach((o) => {
        if (!shown.includes(o.position) && main) values[o.position - 1] = main.options[o.position - 1];
      });
      return values;
    }

    resolveRow(i) {
      const values = this.rowValues(i);
      return this.allVariants.find((v) => v.options.every((o, k) => o === values[k])) || null;
    }

    valueState(i, pos, value) {
      const values = this.rowValues(i);
      values[pos - 1] = value;
      const match = this.allVariants.find((v) => v.options.every((o, k) => o === values[k]));
      return { exists: Boolean(match), available: Boolean(match && match.available) };
    }

    setRowValue(i, pos, value) {
      if (!this.rows[i]) return;
      this.rows[i][pos - 1] = value;
      this.refreshRows();
      this.update();
      const v = this.resolveRow(i);
      if (this.config.rowGallery && v && v.mediaId) {
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          try {
            gallery.setActiveMedia(`${this.sectionId}-${v.mediaId}`, false);
          } catch (e) {
            /* gallery without that media */
          }
        }
      }
    }

    renderRows() {
      this.tiers.forEach((tier) => {
        const wrap = tier.querySelector('[data-rows]');
        const inner = tier.querySelector('[data-rows-inner]');
        const active = tier === this.selected && this.rowsActive(tier);
        const inline = tier.querySelector('[data-gift-inline]');
        if (inline) inline.hidden = active;
        if (!active) {
          wrap.classList.remove('is-open');
          if (inner.childElementCount) {
            const clear = () => {
              if (!wrap.classList.contains('is-open')) inner.innerHTML = '';
            };
            reducedMotion() ? clear() : setTimeout(clear, 360);
          }
          return;
        }
        const q = Number(tier.dataset.qty);
        this.ensureRows(q);
        inner.innerHTML = '';
        for (let i = 0; i < q; i += 1) inner.appendChild(this.buildRow(i, tier, q));
        requestAnimationFrame(() => wrap.classList.add('is-open'));
      });
      this.refreshRows();
    }

    buildRow(i, tier, q) {
      const cfg = this.tierConfig(tier);
      const row = document.createElement('div');
      row.className = 'ex-qb__row';
      row.dataset.rowIndex = i;
      const n = i + 1;
      const title = this.product.title || '';
      const shortTitle = title.length > 20 ? `${title.slice(0, 20).trim()}…` : title;
      row.innerHTML = `
        <div class="ex-qb__row-label">
          ${this.config.rowThumb ? '<span class="ex-qb__row-thumb" data-row-thumb></span>' : ''}
          <span class="ex-qb__circle">1x</span>
          <span class="ex-qb__row-text">
            ${this.config.rowTitle ? `<span class="ex-qb__row-title">${escapeHTML(shortTitle)}</span>` : ''}
            <span class="ex-qb__row-variant" data-row-variant></span>
            <span class="ex-qb__row-error" data-row-error hidden>${escapeHTML(this.strings.unavailable || '')}</span>
          </span>
        </div>
        <div class="ex-qb__row-selectors"></div>`;
      const selectors = row.querySelector('.ex-qb__row-selectors');
      const display = this.config.rowDisplay || [];
      this.shownPositions().forEach((pos) => {
        const option = this.product.options[pos - 1];
        const type = display[pos - 1];
        const aria = fill(this.strings.unitOptions || '[option], item [n]', { option: option.name, n });
        const wrap = document.createElement('span');
        wrap.className = `ex-qb__sel ex-qb__sel--${type}`;
        if (type === 'swatch') selectors.classList.add('has-swatch');
        if (type === 'dropdown') {
          const select = document.createElement('select');
          select.className = 'ex-qb__select';
          select.dataset.row = i;
          select.dataset.pos = pos;
          select.setAttribute('aria-label', aria);
          option.values.forEach((val) => {
            const opt = document.createElement('option');
            opt.value = val.name;
            opt.textContent = val.name;
            select.appendChild(opt);
          });
          wrap.appendChild(select);
        } else {
          const group = document.createElement('div');
          group.className = `ex-qb__choices ex-qb__choices--${type}`;
          group.setAttribute('role', 'group');
          group.setAttribute('aria-label', aria);
          option.values.forEach((val) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = `ex-qb__choice ex-qb__choice--${type}`;
            b.dataset.row = i;
            b.dataset.pos = pos;
            b.dataset.value = val.name;
            b.setAttribute('aria-label', val.name);
            if (type === 'swatch') {
              const dot = document.createElement('span');
              dot.className = 'ex-qb__dot';
              dot.style.setProperty('--sw-color', swatchColour(val));
              if (val.swatchImage) dot.style.backgroundImage = `url("${val.swatchImage}")`;
              b.appendChild(dot);
            } else if (type === 'variant_image') {
              const variant = this.allVariants.find((v) => v.options[pos - 1] === val.name && v.image);
              if (variant) {
                const img = document.createElement('img');
                img.src = variant.image;
                img.alt = '';
                img.width = 34;
                img.height = 34;
                img.loading = 'lazy';
                b.appendChild(img);
              } else if (val.swatchImage || swatchColour(val) !== '#CCCCCC') {
                // No variant photo: a colour dot reads better than a cut-off name
                const dot = document.createElement('span');
                dot.className = 'ex-qb__dot';
                dot.style.setProperty('--sw-color', swatchColour(val));
                if (val.swatchImage) dot.style.backgroundImage = `url("${val.swatchImage}")`;
                b.appendChild(dot);
              } else {
                b.textContent = abbreviateSize(val.name).slice(0, 3);
                b.title = val.name;
              }
            } else {
              b.textContent = abbreviateSize(val.name);
            }
            group.appendChild(b);
          });
          wrap.appendChild(group);
        }
        selectors.appendChild(wrap);
      });
      const giftCount = Math.min(Number(cfg.giftCount) || 0, q);
      if (cfg.giftLabel && i >= q - giftCount) {
        const gift = document.createElement('span');
        gift.className = 'ex-qb__gift';
        gift.innerHTML = `${this.config.giftIcon ? `<img src="${this.config.giftIcon}" alt="" width="16" height="16" loading="lazy">` : ''}<span>${cfg.giftLabel}</span>`;
        row.appendChild(gift);
      }
      return row;
    }

    refreshRows() {
      const tier = this.selected;
      if (!tier || !this.rowsActive(tier)) return;
      tier.querySelectorAll('.ex-qb__row').forEach((row) => {
        const i = Number(row.dataset.rowIndex);
        const values = this.rowValues(i);
        const variant = this.resolveRow(i);
        const ok = Boolean(variant && variant.available);
        row.classList.toggle('is-invalid', !ok);
        const err = row.querySelector('[data-row-error]');
        if (err) {
          err.hidden = ok;
          err.textContent = variant && !variant.available ? this.strings.soldOut || '' : this.strings.unavailable || '';
        }
        const label = row.querySelector('[data-row-variant]');
        if (label) label.textContent = variant ? `(${variant.title})` : `(${values.filter(Boolean).join(' / ')})`;
        const thumb = row.querySelector('[data-row-thumb]');
        if (thumb) {
          const src = (variant && variant.image) || this.product.image;
          if (src && thumb.dataset.src !== src) {
            thumb.dataset.src = src;
            thumb.innerHTML = `<img src="${src}" alt="" width="30" height="30" loading="lazy">`;
          }
        }
        row.querySelectorAll('select[data-row]').forEach((select) => {
          const pos = Number(select.dataset.pos);
          select.value = values[pos - 1];
          [...select.options].forEach((opt) => {
            const st = this.valueState(i, pos, opt.value);
            opt.disabled = !st.available && opt.value !== values[pos - 1];
          });
        });
        row.querySelectorAll('button[data-row]').forEach((b) => {
          const pos = Number(b.dataset.pos);
          const selectedValue = b.dataset.value === values[pos - 1];
          const st = this.valueState(i, pos, b.dataset.value);
          b.setAttribute('aria-pressed', String(selectedValue));
          b.classList.toggle('is-unavailable', !st.available);
          b.disabled = !st.available && !selectedValue;
        });
      });
    }

    /* Pricing ----------------------------------------------------------- */
    unitsFor(tier) {
      const q = Number(tier.dataset.qty);
      const main = this.mainVariant();
      if (tier === this.selected && this.rowsActive(tier)) {
        this.ensureRows(q);
        return Array.from({ length: q }, (_, i) => this.resolveRow(i) || main).filter(Boolean);
      }
      return main ? Array(q).fill(main) : [];
    }

    priceTier(tier) {
      const cfg = this.tierConfig(tier);
      const units = this.unitsFor(tier);
      if (!units.length) return null;
      const q = units.length;
      const prices = units.map((u) => u.price);
      const compares = units.map((u) => (u.compare > u.price ? u.compare : u.price));
      const sumP = sum(prices);
      let total = sumP;
      let custom = false;
      const raw = String(cfg.value == null ? '' : cfg.value).trim();
      if (raw) {
        if (this.config.pricing === 'percentage') {
          const p = parseFloat(raw.replace(',', '.'));
          if (p > 0 && p <= 100) {
            total = Math.round(sumP * (1 - p / 100));
            custom = true;
          }
        } else if (this.config.pricing === 'fixed_unit_price') {
          const a = typedCents(raw);
          if (a !== null && a > 0) {
            total = a * q;
            custom = true;
          }
        } else if (this.config.pricing === 'pay_for_n') {
          const m = parseInt(raw, 10);
          if (m >= 1 && m <= q) {
            total = sum(prices.slice().sort((a, b) => b - a).slice(0, m));
            custom = true;
          }
        }
      }
      let compare;
      const cu = typedCents(cfg.compareUnit);
      if (cu !== null && cu > 0) compare = cu * q;
      else if (this.config.reference === 'regular_price') compare = sumP;
      else compare = sum(compares);
      if (this.engine.subscription && !custom) total = this.engine.subscriptionTotal(units, total);
      return { total, compare, saved: compare - total, q, units };
    }

    update() {
      const main = this.mainVariant();
      const c = this.config;
      this.prices = new Map();
      this.tiers.forEach((tier) => {
        const cfg = this.tierConfig(tier);
        const pr = this.priceTier(tier);
        this.prices.set(tier, pr);
        const q = Number(tier.dataset.qty);
        const allowed = ruleAllows(main, q);
        tier.classList.toggle('is-disabled', !allowed);
        const input = tier.querySelector('[data-ex-tier-input]');
        if (input) input.disabled = !allowed;
        const note = tier.querySelector('[data-rule-note]');
        if (note) note.hidden = allowed;
        if (!pr) return;
        const showCompare = c.showCompare && !cfg.hideCompare && pr.compare > pr.total && pr.compare > 0;
        const priceEl = tier.querySelector('[data-price]');
        if (priceEl) priceEl.innerHTML = money(pr.total);
        const cmpEl = tier.querySelector('[data-compare]');
        if (cmpEl) {
          cmpEl.hidden = !showCompare;
          cmpEl.innerHTML = money(pr.compare);
        }
        const saveEl = tier.querySelector('[data-save]');
        if (saveEl) {
          const show = c.showSave && pr.saved > 0;
          saveEl.hidden = !show;
          if (show) {
            saveEl.textContent = `${c.saveLabel} ${c.saveFormat === 'percent' ? `${savePct(pr.compare, pr.total)}%` : textOf(money(pr.saved))}`;
          }
        }
        const savingsEl = tier.querySelector('[data-savings]');
        if (savingsEl) {
          let text = '';
          const savedMoney = money(Math.max(0, pr.saved));
          if (cfg.subtitle) text = String(cfg.subtitle).split('{savings}').join(savedMoney);
          else if (c.showSavingsLine && pr.saved > 0) text = String(c.savingsFormat || '').split('{savings}').join(savedMoney);
          savingsEl.hidden = !text;
          savingsEl.innerHTML = text;
        }
        const perItem = tier.querySelector('[data-per-item-amount]');
        if (perItem) perItem.innerHTML = money(Math.round(pr.total / pr.q));
      });
      if (this.selected && this.selected.classList.contains('is-disabled')) {
        const first = this.tiers.find((t) => !t.classList.contains('is-disabled'));
        if (first && first !== this.selected) {
          this.select(first);
          return;
        }
      }
      this.engine.schedule();
    }

    offerState() {
      const tier = this.selected;
      if (!tier) return null;
      const cfg = this.tierConfig(tier);
      const pr = (this.prices && this.prices.get(tier)) || this.priceTier(tier);
      const q = Number(tier.dataset.qty);
      const main = this.mainVariant();
      let lines = [];
      let valid = true;
      let error = '';
      if (this.rowsActive(tier)) {
        this.ensureRows(q);
        const variants = Array.from({ length: q }, (_, i) => this.resolveRow(i));
        if (variants.some((v) => !v || !v.available)) {
          valid = false;
          error = this.strings.unavailable || '';
        }
        const order = [];
        const counts = new Map();
        variants.filter(Boolean).forEach((v) => {
          if (!counts.has(v.id)) order.push(v.id);
          counts.set(v.id, (counts.get(v.id) || 0) + 1);
        });
        lines = order.map((id) => ({ id, quantity: counts.get(id) }));
      } else if (this.engine.variant) {
        lines = [{ id: this.engine.variant.id, quantity: q }];
      }
      if (!ruleAllows(main, q)) {
        valid = false;
        error = this.strings.qtyRule || '';
      }
      const showCompare = this.config.showCompare && !cfg.hideCompare;
      return {
        lines,
        label: cfg.label || `${q}x`,
        tag: Boolean(this.config.tag),
        quantity: q,
        totalCents: pr ? pr.total : null,
        compareCents: pr ? (showCompare ? pr.compare : pr.total) : null,
        valid,
        error,
      };
    }

    onVariantChange(variant, prev) {
      if (variant && prev && this.rows.length) {
        const shown = this.shownPositions();
        const q = this.selected ? Number(this.selected.dataset.qty) : 0;
        variant.options.forEach((value, k) => {
          if (prev.options[k] === value || !shown.includes(k + 1)) return;
          for (let i = 0; i < q && i < this.rows.length; i += 1) this.rows[i][k] = value;
        });
      }
      this.refreshRows();
      this.update();
    }

    onSubscriptionChange() {
      this.update();
    }

    revealError() {
      const bad = this.querySelector('.ex-qb__row.is-invalid, .ex-qb__tier.is-selected [data-rule-note]:not([hidden])');
      if (bad && typeof bad.scrollIntoView === 'function') bad.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  }
  if (!customElements.get('elexa-qty-breaks')) customElements.define('elexa-qty-breaks', QtyBreaks);

  /* ------------------------------------------------------------------ *
   * <elexa-subscribe-save>: Subscribe and save (custom / dynamic)
   * ------------------------------------------------------------------ */
  class SubscribeSave extends OfferBlock {
    init() {
      this.mainProductData = readJSON(this, ':scope > script[data-ex-product]');
      this.product = this.mainProductData || { variants: [], groups: [] };
      this.plans = new Map();
      (this.product.groups || []).forEach((g) =>
        (g.plans || []).forEach((p) => this.plans.set(String(p.id), Object.assign({ groupId: g.id, groupName: g.name }, p)))
      );
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.cards.forEach((card) => {
        const select = card.querySelector('[data-freq-select]');
        card.planId = card.dataset.planId;
        if (select && select.value && this.plans.has(select.value)) card.planId = select.value;
        card.valid = this.plans.has(String(card.planId));
      });
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-card]');
        if (!card) return;
        const onSwitch = e.target.closest('[data-switch]');
        if (!onSwitch && e.target.closest('a, button, select, input, label, [data-freq]')) return;
        this.toggle(card);
      });
      this.addEventListener('change', (e) => {
        if (!e.target.matches('[data-freq-select]')) return;
        const card = e.target.closest('[data-card]');
        const value = e.target.value;
        if (value && this.plans.has(value)) {
          card.planId = value;
          card.valid = true;
          if (card.classList.contains('is-on')) this.publish();
          this.updatePrices();
        }
      });
    }

    afterRegister() {
      const cfg = this.config;
      const initial = this.cards.find((c) => c.classList.contains('is-on') && c.valid) || null;
      if (cfg.requires && !initial) {
        const first = this.cards.find((c) => c.valid);
        if (first) return this.setActive(first);
      }
      this.setActive(initial);
    }

    toggle(card) {
      if (!card.valid) return;
      const on = card.classList.contains('is-on');
      if (on && this.config.requires) return;
      this.setActive(on ? null : card);
    }

    setActive(card) {
      this.active = card;
      this.cards.forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-on', on);
        const sw = c.querySelector('[data-switch]');
        if (sw) sw.setAttribute('aria-checked', String(on));
        if (on && this.config.requires) c.classList.add('is-locked');
      });
      this.publish();
      this.updatePrices();
    }

    publish() {
      const card = this.active;
      if (!card) {
        this.engine.setSubscription(this, null);
        this.engine.clearBaseQuantity(this);
        return;
      }
      const plan = this.plans.get(String(card.planId));
      if (!plan) return;
      this.engine.setSubscription(this, {
        planId: String(plan.id),
        groupId: plan.groupId,
        name: plan.name,
        type: plan.type,
        value: Number(plan.value) || 0,
      });
      const qty = Number(this.config.subQty) || 1;
      if (this.config.mode === 'custom' && qty > 1 && !this.engine.activeQuantityOffer()) {
        this.engine.setBaseQuantity(qty, this);
      }
    }

    displayQuantity() {
      const qo = this.engine.activeQuantityOffer();
      if (qo && qo.selected) return Number(qo.selected.dataset.qty) || 1;
      const sub = Number(this.config.subQty) || 1;
      return sub > 1 ? sub : this.engine.baseQuantity();
    }

    updatePrices() {
      if (this.config.priceDisplay === 'custom_text') return;
      const v = this.engine.displayVariant;
      if (!v) return;
      this.cards.forEach((card) => {
        const plan = this.plans.get(String(card.planId));
        const priceEl = card.querySelector('[data-price]');
        const cmpEl = card.querySelector('[data-compare]');
        if (!priceEl) return;
        const unit = plan ? this.engine.subscriptionUnit(v, { planId: String(plan.id), type: plan.type, value: Number(plan.value) || 0 }) : v.price;
        let price;
        let compare;
        if (this.config.mode === 'custom') {
          const q = this.displayQuantity();
          price = unit * q;
          compare = v.price * q;
        } else {
          price = unit;
          compare = Math.max(v.compare || 0, v.price);
        }
        priceEl.innerHTML = money(price);
        if (cmpEl) {
          cmpEl.hidden = !(compare > price);
          cmpEl.innerHTML = money(compare);
        }
      });
    }

    onVariantChange() {
      this.updatePrices();
    }

    onOfferChange() {
      this.updatePrices();
    }
  }
  if (!customElements.get('elexa-subscribe-save')) customElements.define('elexa-subscribe-save', SubscribeSave);

  /* ------------------------------------------------------------------ *
   * <elexa-gift-kit>: gift lines added with the main Add to cart
   * ------------------------------------------------------------------ */
  class GiftKit extends OfferBlock {
    afterRegister() {
      const gifts = readJSON(this, ':scope > script[data-ex-gifts]') || [];
      this.engine.setGifts(this, gifts);
    }
  }
  if (!customElements.get('elexa-gift-kit')) customElements.define('elexa-gift-kit', GiftKit);

  /* ------------------------------------------------------------------ *
   * Shared bits for independent add buttons
   * ------------------------------------------------------------------ */
  function flash(el, cls, ms, done) {
    el.classList.add(cls);
    clearTimeout(el.exFlashTimer);
    el.exFlashTimer = setTimeout(() => {
      el.classList.remove(cls);
      if (done) done();
    }, ms);
  }

  /* ------------------------------------------------------------------ *
   * <elexa-upsell>: Product upsell
   * ------------------------------------------------------------------ */
  class Upsell extends OfferBlock {
    init() {
      this.product = readJSON(this, ':scope > script[data-ex-product]') || { variants: [] };
      this.variantsList = (this.product.variants || []).map(normaliseVariant);
      this.variant = this.variantsList.find((v) => v.id === Number(this.config.initial)) || this.variantsList[0] || null;
      this.strings = this.config.strings || {};
      this.button = this.querySelector('[data-add]');
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.checkbox = this.querySelector('[data-with-main]');
      this.image = this.querySelector('.ex-up__image');
      this.errorEl = this.parentElement && this.parentElement.querySelector(':scope > [data-error]');
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-pos]')) this.onOptionChange();
        else if (e.target === this.checkbox) this.syncAddon();
      });
      if (this.button) this.button.addEventListener('click', () => this.add());
      if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this.toggle());
    }

    connect() {
      this.listen(document, EVENTS.cartUpdated, (e) => this.syncFromCart(e.detail && e.detail.cart));
    }

    afterRegister() {
      this.syncAddon();
    }

    onOptionChange() {
      this.showError('');
      const values = [...this.querySelectorAll('[data-option-pos]')]
        .sort((a, b) => a.dataset.optionPos - b.dataset.optionPos)
        .map((s) => s.value);
      this.variant = this.variantsList.find((v) => v.options.every((o, k) => o === values[k])) || null;
      this.render();
      this.syncAddon();
      if (this.toggleBtn && Elexa.cart && Elexa.cart.lastCart) this.syncFromCart(Elexa.cart.lastCart);
    }

    render() {
      const v = this.variant;
      const ok = Boolean(v && v.available);
      this.classList.toggle('is-sold-out', !ok);
      this.querySelectorAll('[data-price]').forEach((el) => {
        el.innerHTML = v ? money(v.price) : '';
      });
      this.querySelectorAll('[data-compare]').forEach((el) => {
        const show = Boolean(this.config.showCompare && v && v.compare > v.price);
        el.hidden = !show;
        if (v) el.innerHTML = money(v.compare);
      });
      if (this.button) {
        this.button.disabled = !ok;
        const label = this.button.querySelector('[data-label]');
        if (label) {
          if (!ok) label.textContent = this.strings.soldOut || '';
          else if (this.config.priceBelow) label.textContent = this.config.buttonText || '';
          else label.innerHTML = `<span data-price>${money(v.price)}</span>`;
        }
      }
      if (this.toggleBtn) this.toggleBtn.disabled = !ok;
      if (this.checkbox) {
        this.checkbox.disabled = !ok;
        if (!ok) this.checkbox.checked = false;
      }
      if (this.image && v && v.image) {
        this.image.src = v.image;
        this.image.removeAttribute('srcset');
      }
    }

    /** Shopify's message (e.g. "You can only add 5 of this item") under the card; blank hides it. */
    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    syncAddon() {
      if (!this.checkbox) return;
      const ok = this.variant && this.variant.available;
      this.engine.setAddons(this, this.checkbox.checked && ok ? [{ id: this.variant.id, quantity: 1 }] : []);
    }

    async add() {
      const v = this.variant;
      if (!v || !v.available || this.busy) return;
      this.busy = true;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      const feedback = this.button.querySelector('[data-feedback]');
      this.showError('');
      try {
        await Elexa.cart.add([{ id: v.id, quantity: 1 }], { open: this.config.afterAdd === 'open_drawer', source: 'upsell' });
        this.button.classList.remove('is-loading');
        if (feedback) feedback.textContent = this.strings.added || '';
        flash(this.button, 'is-success', 2000, () => feedback && (feedback.textContent = ''));
      } catch (e) {
        this.button.classList.remove('is-loading');
        if (feedback) feedback.textContent = this.strings.error || '';
        if (e && e.message) this.showError(e.message);
        flash(this.button, 'is-error', 2000, () => feedback && (feedback.textContent = ''));
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    findLine(cart) {
      if (!cart || !this.variant) return null;
      return (
        (cart.items || []).find(
          (i) => i.variant_id === this.variant.id && !(i.properties && (i.properties._elexa_gift || i.properties._elexa_bundle))
        ) || null
      );
    }

    setToggle(on) {
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-checked', String(on));
    }

    async toggle() {
      const v = this.variant;
      if (!v || this.busy) return;
      const on = this.toggleBtn.getAttribute('aria-checked') === 'true';
      this.busy = true;
      this.toggleBtn.classList.add('is-loading');
      this.toggleBtn.setAttribute('aria-busy', 'true');
      this.setToggle(!on);
      this.showError('');
      try {
        if (!on) {
          if (!v.available) throw new Error('sold out');
          await Elexa.cart.add([{ id: v.id, quantity: 1 }], { open: this.config.afterAdd === 'open_drawer', source: 'upsell' });
        } else {
          const cart = await Elexa.cart.get();
          const line = this.findLine(cart);
          if (line) await Elexa.cart.change(line.key, Math.max(0, line.quantity - 1), { source: 'upsell' });
        }
      } catch (e) {
        this.setToggle(on);
        if (e && e.message && e.message !== 'sold out') this.showError(e.message);
        flash(this, 'is-error', 600);
      } finally {
        this.busy = false;
        this.toggleBtn.classList.remove('is-loading');
        this.toggleBtn.removeAttribute('aria-busy');
      }
    }

    syncFromCart(cart) {
      if (!this.toggleBtn || this.busy || !cart) return;
      this.setToggle(Boolean(this.findLine(cart)));
    }
  }
  if (!customElements.get('elexa-upsell')) customElements.define('elexa-upsell', Upsell);

  /* ------------------------------------------------------------------ *
   * <elexa-bundle-save>: Bundle and save (independent add + discount code)
   * ------------------------------------------------------------------ */
  class BundleSave extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.init();
      }
      this.onCart = this.onCart || ((e) => this.syncFromCart(e.detail && e.detail.cart));
      document.addEventListener(EVENTS.cartUpdated, this.onCart);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENTS.cartUpdated, this.onCart);
    }

    init() {
      this.config = readJSON(this, ':scope > script[data-ex-config]') || {};
      this.blockId = this.dataset.blockId;
      this.rows = [...this.querySelectorAll('[data-row]')].map((el) => {
        const variants = (readJSON(el, 'script[data-ex-variants]') || []).map(normaliseVariant);
        const select = el.querySelector('[data-variant-select]');
        const check = el.querySelector('[data-check]');
        let variant = variants.find((v) => select && v.id === Number(select.value)) || variants.find((v) => v.available) || variants[0];
        return { el, variants, select, check, variant, checked: el.dataset.checked === 'true' };
      });
      this.button = this.querySelector('[data-add]');
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.status = this.querySelector('[data-status]');
      this.errorEl = this.querySelector('[data-error]');

      this.addEventListener('change', (e) => {
        const rowEl = e.target.closest('[data-row]');
        const row = rowEl && this.rows.find((r) => r.el === rowEl);
        if (!row) return;
        if (e.target === row.select) {
          row.variant = row.variants.find((v) => v.id === Number(row.select.value)) || row.variant;
        } else if (e.target === row.check) {
          row.checked = row.check.checked;
        }
        this.update();
      });
      this.addEventListener('click', (e) => {
        const rowEl = e.target.closest('[data-row]');
        if (!rowEl || e.target.closest('select, input, a, button, label')) return;
        const row = this.rows.find((r) => r.el === rowEl);
        if (row && row.check && !row.check.disabled) {
          row.check.checked = !row.check.checked;
          row.checked = row.check.checked;
          this.update();
        }
      });
      if (this.button) this.button.addEventListener('click', () => this.add());
      if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this.toggle());
    }

    selectedRows() {
      return this.rows.filter((r) => r.variant && r.variant.available && (r.check ? r.checked : true));
    }

    update() {
      this.rows.forEach((r) => {
        const v = r.variant;
        if (!v) return;
        const cmp = v.compare > v.price ? v.compare : v.price;
        const priceEl = r.el.querySelector('[data-price]');
        if (priceEl) priceEl.innerHTML = money(v.price);
        const cmpEl = r.el.querySelector('[data-compare]');
        if (cmpEl) {
          cmpEl.hidden = !(cmp > v.price);
          cmpEl.innerHTML = money(cmp);
        }
        const save = r.el.querySelector('[data-save]');
        if (save) {
          save.hidden = !(cmp > v.price);
          const amount = save.querySelector('[data-save-amount]');
          if (amount) amount.innerHTML = money(cmp - v.price);
        }
        const img = r.el.querySelector('.ex-bs__row-img');
        if (img && v.image && img.dataset.variantSrc !== v.image) {
          img.dataset.variantSrc = v.image;
          img.src = v.image;
          img.removeAttribute('srcset');
        }
        r.el.classList.toggle('is-unchecked', Boolean(r.check) && !r.checked);
      });
      const picked = this.selectedRows();
      const total = sum(picked.map((r) => r.variant.price));
      let compare = sum(picked.map((r) => Math.max(r.variant.compare || 0, r.variant.price)));
      let shownTotal = total;
      const pct = Number(this.config.discountPct) || 0;
      if (pct > 0) {
        shownTotal = Math.round(total * (1 - pct / 100));
        compare = Math.max(compare, total);
      }
      const saved = compare - shownTotal;
      const set = (sel, html) => this.querySelectorAll(sel).forEach((el) => (el.innerHTML = html));
      set('[data-count]', String(picked.length));
      set('[data-total], [data-btn-total]', money(shownTotal));
      set('[data-compare-total]', money(compare));
      set('[data-saved-amount]', money(Math.max(0, saved)));
      this.querySelectorAll('[data-compare-total], [data-saved]').forEach((el) => (el.hidden = !(saved > 0)));
      if (this.button && !this.busy) this.button.disabled = picked.length === 0;
    }

    items() {
      const props = { _elexa_offer: this.config.label || 'Bundle', _elexa_bundle: this.blockId };
      return this.selectedRows().map((r) => ({ id: r.variant.id, quantity: 1, properties: Object.assign({}, props) }));
    }

    async addToCart() {
      const items = this.items();
      if (!items.length) throw new Error('empty');
      await Elexa.cart.add(items, { open: false, source: 'bundle' });
      if (this.config.code) {
        try {
          await Elexa.cart.applyDiscount(this.config.code, { source: 'bundle' });
        } catch (e) {
          console.warn('[Elexa offers] bundle code not applied:', e && e.message);
        }
      }
      if (this.config.afterAdd === 'open_drawer' && Elexa.cart.open) Elexa.cart.open();
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    async add() {
      if (this.busy || !this.button || this.button.disabled) return;
      this.busy = true;
      this.showError('');
      const feedback = this.button.querySelector('[data-feedback]');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      try {
        await this.addToCart();
        this.button.classList.remove('is-loading');
        if (feedback) feedback.textContent = this.config.success || '';
        flash(this.button, 'is-success', 2500, () => feedback && (feedback.textContent = ''));
      } catch (e) {
        this.button.classList.remove('is-loading');
        if (feedback) feedback.textContent = this.config.error || '';
        if (e && e.message && e.message !== 'empty') this.showError(e.message);
        flash(this.button, 'is-error', 2000, () => feedback && (feedback.textContent = ''));
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        this.update();
      }
    }

    setStatus(text, persist) {
      if (!this.status) return;
      this.status.textContent = text || '';
      this.status.classList.add('is-visible');
      clearTimeout(this.statusTimer);
      if (!persist) this.statusTimer = setTimeout(() => this.status.classList.remove('is-visible'), 2000);
    }

    async toggle() {
      if (this.busy) return;
      const on = this.toggleBtn.getAttribute('aria-checked') === 'true';
      this.busy = true;
      this.showError('');
      this.toggleBtn.setAttribute('aria-checked', String(!on));
      this.toggleBtn.classList.add('is-loading');
      this.setStatus(on ? this.config.removing : this.config.adding, true);
      try {
        if (!on) {
          await this.addToCart();
        } else {
          const cart = await Elexa.cart.get();
          const updates = {};
          (cart.items || []).forEach((i) => {
            if (i.properties && i.properties._elexa_bundle === this.blockId) updates[i.key] = 0;
          });
          if (Object.keys(updates).length) await Elexa.cart.update({ updates }, { source: 'bundle' });
        }
        this.setStatus(on ? this.config.removed : this.config.added);
      } catch (e) {
        this.toggleBtn.setAttribute('aria-checked', String(on));
        this.setStatus(this.config.error);
        if (e && e.message && e.message !== 'empty') this.showError(e.message);
      } finally {
        this.busy = false;
        this.toggleBtn.classList.remove('is-loading');
      }
    }

    syncFromCart(cart) {
      if (!this.toggleBtn || this.busy || !cart) return;
      const on = (cart.items || []).some((i) => i.properties && i.properties._elexa_bundle === this.blockId);
      this.toggleBtn.setAttribute('aria-checked', String(on));
    }
  }
  if (!customElements.get('elexa-bundle-save')) customElements.define('elexa-bundle-save', BundleSave);

})();
